// # 문 — 앉을 자리가 있으면 들여보내고, 없으면 로그인 화면을 준다
//
// ## 새로고침이 로그아웃이 되지 않게
// 접근 토큰은 메모리에만 있으니 새로고침하면 사라진다. 그래서 문을 열기 전에
// **쿠키 한 장으로 한 번 갱신을 시도한다.** 성공하면 그대로 들어가고, 실패하면
// 로그인 화면이다. `localStorage` 를 쓰지 않고도 세션이 이어지는 값이 여기 있다.
//
// ## 화면이 알아야 할 실패는 문장이지 상태 코드가 아니다
// `ApiError` 를 그대로 내려보내면 화면이 숫자를 해석해야 한다. 여기서 한 문장으로
// 바꿔 넘긴다 — 로그인 화면은 네트워크를 모르는 채로 남는다.

import {
  ApiError,
  type AuthApi,
  type GoogleDone,
  is_google_signup,
  is_second,
  type LoginResult,
  type OrgDoor,
  type Seat,
  type Session,
  type SignedIn,
} from '@maru/client'
import type { Platform } from '@maru/platform'
import type { ChannelId } from '@maru/protocol'
import {
  locale_now,
  msg,
  type OrgHands,
  type SettingsTab,
  Shell,
  Spinner,
  type Stage,
  set_locale,
  translate,
  trouble,
} from '@maru/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useState } from 'react'
import { type ConsentIntent, consent_in } from './consent_intent.ts'
import { DraftBook } from './drafts.ts'
import {
  apply_look,
  apply_scheme,
  boot_display,
  doc_root,
  load_scheme,
  reset_look,
} from './look.ts'
import { forget_orgs, keep_orgs, load_orgs, type Org } from './orgs.ts'
import { useOutbound } from './outbound.ts'
import { put_hash, read_settings, settings_hash } from './permalink.ts'
import { drop_push, usePush } from './push.ts'
import { share_in } from './share.ts'

// 설정은 첫 화면에 없다 — 관리 패널 여럿을 끌고 오므로 열 때 따로 받는다.
const Settings = lazy(() => import('./settings.tsx').then((m) => ({ default: m.Settings })))
// 문 앞 화면(로그인·동의·2단계 설정)도 첫 화면에 없다 — 들어와 있는 사람은 받지 않는다. 기다리는
// 동안은 갱신 대기와 같은 도는 표시라, 새로 온 사람에게도 화면이 한 번 더 바뀌지 않는다.
const SignIn = lazy(() => import('@maru/ui/sign_in').then((m) => ({ default: m.SignIn })))
// 공유 시트로 들어왔을 때만 선다 — 첫 화면에 싣지 않는다.
const SharePick = lazy(() => import('@maru/ui/share_pick').then((m) => ({ default: m.SharePick })))
const InviteHere = lazy(() =>
  import('./invite_here.tsx').then((m) => ({ default: m.InviteHereGate })),
)
const JoinGate = lazy(() => import('./join_gate.tsx').then((m) => ({ default: m.JoinGate })))
const Consent = lazy(() => import('./consent.tsx').then((m) => ({ default: m.Consent })))
const EnrollFirst = lazy(() => import('./two_factor.tsx').then((m) => ({ default: m.EnrollFirst })))

/** 설정을 열며 쌓은 이력 칸의 표식. 이 칸에서 닫으면 한 칸 되돌아간다. */
const SETTINGS_STEP = 'maru.settings'

/** 문이 아직 모를 때 — 갱신 한 번, 또는 문 앞 화면을 받는 중. */
function Waiting({ stranded = false }: { stranded?: boolean }) {
  return (
    <main
      className="AuthFrame"
      aria-busy="true"
      aria-label={translate(locale_now(), msg('로그인하는 중'))}
    >
      <Spinner size="lg" className="AuthFrame-wait" />
      {stranded && (
        <p className="AuthFrame-wait" role="status">
          {translate(locale_now(), msg('서버에 닿지 못했어요. 잠시 뒤 저절로 다시 시도해요.'))}
        </p>
      )}
    </main>
  )
}

/** 갱신이 망·서버 탓으로 실패했을 때 다시 묻는 간격. 오래 끊겨도 30초마다는 묻는다. */
const RESUME_RETRY_MS = [2_000, 5_000, 10_000, 30_000] as const

import { Room } from './room.tsx'

/**
 * 문 앞의 실패. 404·400 은 로그인에서 늘 "코드나 주소가 틀렸다" 는 뜻이다 — 계정이 없는지
 * 코드가 틀렸는지는 서버가 가르지 않는다. 409 의 SSO 이유(`sso_required`…)는 비밀번호를
 * 맞힌 사람에게 알려 주어도 새는 것이 없으니 `trouble` 이 이유 표로 옮긴다.
 */
function why(e: unknown): string {
  if (e instanceof ApiError && (e.status === 404 || e.status === 400))
    return translate(locale_now(), msg('코드나 주소가 맞지 않아요. 확인한 뒤 다시 입력해 주세요.'))
  return trouble(e)
}

export interface GateProps {
  auth: AuthApi
  seat: Seat
  session: Session
  platform: Platform
  /** 초대 링크로 왔다면 그 토큰. 기본은 주소창(`/invite/<토큰>` 또는 `?invite=`). */
  invite_token?: string | null
  /** 공유 초대 링크(G30)로 왔다면 그 토큰. 기본은 주소창(`/join/<토큰>`). */
  join_token?: string | null
  /** 동의 부탁. 기본은 주소창(`/oauth/authorize?client_id=…`). */
  consent?: ConsentIntent | null
  /** 공유 시트로 들어온 글. 기본은 주소창(`/share?text=…`). */
  shared?: string | null
  /** 조직 주소(`/w/{slug}`·`{slug}.도메인`)로 왔다면 그 slug. 기본은 주소창. */
  org_slug?: string | null
  /** 서브도메인 꼴을 볼 조직 도메인(`VITE_MARU_ORG_DOMAIN`). 없으면 경로 꼴만 본다. */
  org_domain?: string | null
}

/** 주소창에서 초대 토큰을 꺼낸다. 메일의 링크는 `/invite/{tenant}.{secret}` 이다. */
function invite_in(loc: { pathname: string; search: string } | undefined): string | null {
  if (loc === undefined) return null
  const from_path = /^\/invite\/([^/?#]+)/.exec(loc.pathname)
  if (from_path?.[1]) return decodeURIComponent(from_path[1])
  return new URLSearchParams(loc.search).get('invite')
}

/** 주소창에서 공유 초대 링크(G30)의 토큰을 꺼낸다. 메일 초대(`/invite/`)와 토큰 모양이 같아 경로로 가른다. */
function join_in(loc: { pathname: string } | undefined): string | null {
  const got = loc === undefined ? null : /^\/join\/([^/?#]+)/.exec(loc.pathname)?.[1]
  return got ? decodeURIComponent(got) : null
}

/** 들어와 있는 채 연 초대 — 메일 초대인지 공유 링크인지와 그 토큰. */
type Invited = { token: string; join: boolean }

/** IdP 에서 돌아온 자리(`/sso/callback?code&state`). 아니면 `null`. */
export function sso_callback_in(
  loc: { pathname: string; search: string } | undefined,
): { code: string; state: string } | { denied: true } | null {
  if (loc === undefined || loc.pathname !== '/sso/callback') return null
  const q = new URLSearchParams(loc.search)
  const code = q.get('code')
  const state = q.get('state')
  if (code && state) return { code, state }
  // IdP 가 `error=access_denied` 따위로 돌려보냈다.
  return { denied: true }
}

/** Google 에서 돌아온 자리(`/google/callback?code&state`, 앱은 `maru://auth/google?…`). 아니면 `null`. */
export function google_callback_in(
  loc: { pathname: string; search: string } | undefined,
): { code: string; state: string } | { denied: true } | null {
  if (loc === undefined || loc.pathname !== '/google/callback') return null
  const q = new URLSearchParams(loc.search)
  const code = q.get('code')
  const state = q.get('state')
  if (code && state) return { code, state }
  return { denied: true }
}

/** 앱 딥링크 주소를 웹 콜백과 같은 꼴로 읽는다. */
export function google_link_in(
  url: string,
): { code: string; state: string } | { denied: true } | null {
  if (!url.startsWith('maru://auth/google')) return null
  const at = url.indexOf('?')
  return google_callback_in({ pathname: '/google/callback', search: at < 0 ? '' : url.slice(at) })
}

/** 시작한 이 클라이언트만 끝낼 수 있게 짓는 비밀. 같은 탭(웹)·같은 웹뷰(앱)의 세션 저장소에만 둔다. */
const GOOGLE_SECRET_KEY = 'maru.google'

function google_secret_new(): string {
  const raw = new Uint8Array(32)
  crypto.getRandomValues(raw)
  let bin = ''
  for (const b of raw) bin += String.fromCharCode(b)
  const secret = btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  globalThis.sessionStorage?.setItem(GOOGLE_SECRET_KEY, secret)
  return secret
}

/** 비밀을 꺼내며 지운다 — 한 번 끝내면 다시 쓸 일이 없다. */
function google_secret_take(): string | null {
  const secret = globalThis.sessionStorage?.getItem(GOOGLE_SECRET_KEY) ?? null
  globalThis.sessionStorage?.removeItem(GOOGLE_SECRET_KEY)
  return secret
}

/** 설정 화면. 채널 목록은 연동 폼의 고르기 칸에 쓴다. */
function Inside({
  auth,
  seat,
  session,
  platform,
  leave,
  consent,
  enroll,
  shared,
  invite,
}: GateProps & {
  leave(): void
  consent: ConsentIntent | null
  enroll: boolean
  shared: string | null
  /** 들어와 있는 채 연 초대 링크(3차 F215·G30). */
  invite: Invited | null
}) {
  useOutbound(platform)
  usePush(platform.push, session.api, platform.keep)
  // 사이드바 맨 위의 워크스페이스 이름 — 로그인 때 적어 둔 조직 목록에서 지금 조직을 찾는다(3차 F31).
  const [orgs, set_orgs] = useState<readonly Org[]>([])
  useEffect(() => {
    let alive = true
    void load_orgs(platform.keep).then((v) => alive && set_orgs(v))
    return () => {
      alive = false
    }
  }, [platform])
  const tenant = seat.who()?.tenant
  const here_org = orgs.find((o) => o.tenant === tenant)
  const org: OrgHands | undefined =
    here_org === undefined
      ? undefined
      : {
          name: here_org.org_name,
          others: orgs
            .filter((o) => o.tenant !== tenant)
            .map((o) => ({ tenant: o.tenant, name: o.org_name })),
          // 갈아타면 소켓·캐시가 전부 앞 조직의 것이다 — 새 토큰으로 처음부터 다시 연다.
          create: () => globalThis.location?.assign('/?new'),
          switch: async (to) => {
            await seat.switch_org(to)
            queries.clear()
            globalThis.location?.reload()
          },
          settings: () => open_settings(),
          // 설정 청크(`is_admin`)를 첫 화면에 끌고 오지 않으려고 같은 판정을 여기 적는다.
          ...(['admin', 'owner'].includes(seat.who()?.role ?? '')
            ? { invite: () => open_settings('members') }
            : {}),
        }
  // 주소가 환경설정 자리(`#/settings/…`)면 설정부터 — 새로 고쳐도 보던 탭으로 돌아온다(3차 F48).
  const [view, set_view] = useState<'room' | 'settings'>(() =>
    read_settings(globalThis.location?.hash ?? '') === null ? 'room' : 'settings',
  )
  const queries = useQueryClient()
  // 사이드바 아래 내 자리의 로그아웃 — 설정의 것과 같은 순서다: 푸시 토큰, 자리, 캐시, 문.
  const sign_out = async () => {
    await drop_push(session.api, platform.keep)
    await seat.clear()
    queries.clear()
    leave()
  }
  // 설정을 열 때 고른 탭(`open_settings('notifications')`). 없으면 첫 탭.
  const [tab, set_tab] = useState<SettingsTab | undefined>(() => {
    const at = read_settings(globalThis.location?.hash ?? '')
    return at === null || at === '' ? undefined : (at as SettingsTab)
  })
  // 설정을 열면 이력을 한 칸 쌓는다 — 브라우저의 뒤로 가기가 앱을 떠나지 않고 설정을 닫는다(3차 F133).
  // 탭을 옮기는 것은 그 칸을 갈아 끼울 뿐이라(`put_hash`) 뒤로 한 번이면 채널이다.
  const [trip, set_trip] = useState(0)
  // 열자마자 내려갈 섹션(내 메뉴의 [상태 설정] → 프로필의 상태 칸, 3차 F257).
  const [aim, set_aim] = useState<string | undefined>(undefined)
  const open_settings = (to?: SettingsTab, section?: string) => {
    globalThis.history?.pushState(
      SETTINGS_STEP,
      '',
      to === undefined ? '#/settings' : settings_hash(to),
    )
    set_tab(to)
    set_aim(section)
    set_view('settings')
  }
  const close_settings = () => {
    if (globalThis.history?.state === SETTINGS_STEP) {
      globalThis.history.back()
      return
    }
    put_hash('')
    set_view('room')
  }
  // 뒤로·앞으로는 주소가 정한다. 설정 자리가 아니면 닫고, 설정 자리면 그 탭으로 다시 연다 — 주소와 화면이
  // 어긋나면(주소는 프로필인데 화면은 알림) 새로 고칠 때 엉뚱한 탭이 선다.
  useEffect(() => {
    const on_pop = () => {
      const at = read_settings(globalThis.location?.hash ?? '')
      if (at === null) {
        set_view('room')
        return
      }
      set_tab(at === '' ? undefined : (at as SettingsTab))
      set_trip((n) => n + 1)
      set_view('settings')
    }
    globalThis.addEventListener?.('popstate', on_pop)
    return () => globalThis.removeEventListener?.('popstate', on_pop)
  }, [])
  const [asking, set_asking] = useState(consent)
  const [enrolling, set_enrolling] = useState(enroll)
  const [sharing, set_sharing] = useState(shared)
  // 들어와 있는 채 초대 링크를 열면 예전엔 아무 말 없이 채널이 떴다(3차 F215). 어디로 온 초대인지 보여 주고 고르게 한다.
  const [invited, set_invited] = useState(invite)
  const [start, set_start] = useState<ChannelId | null>(null)
  const me = seat.who()?.user ?? 0
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => session.api.my_channels() })
  // 테마·강조색·언어는 이 조직의 것이다. 받아 오는 대로 뿌리에 꽂는다.
  const look = useQuery({ queryKey: ['appearance'], queryFn: () => session.api.appearance() })
  useEffect(() => {
    const root = doc_root()
    if (look.data === undefined) return
    if (root !== null) apply_look(root, look.data)
    set_locale(look.data.locale)
  }, [look.data])
  // 조직이 2단계 인증을 요구하는데 막 들어온 사람은 설정부터 — 안 켜면 다음 갱신에서 밀려난다.
  if (enrolling) {
    return (
      <Suspense fallback={<Waiting />}>
        <EnrollFirst api={session.api} done={() => set_enrolling(false)} />
      </Suspense>
    )
  }
  if (asking !== null) {
    return (
      <Suspense fallback={<Waiting />}>
        <Consent
          session={session}
          intent={asking}
          me={me}
          close={() => {
            // 접었으면 주소창도 치운다. 새로고침이 죽은 부탁을 다시 열면 안 된다.
            globalThis.history?.replaceState(null, '', '/')
            set_asking(null)
          }}
        />
      </Suspense>
    )
  }
  if (invited !== null) {
    return (
      <Suspense fallback={<Waiting />}>
        <InviteHere
          auth={auth}
          session={session}
          me={me}
          token={invited.token}
          join={invited.join}
          stay={() => {
            // 주소창의 링크를 치운다 — 새로 고칠 때마다 이 화면이 다시 서면 안 된다.
            globalThis.history?.replaceState(null, '', '/')
            set_invited(null)
          }}
          // 주소창의 링크는 그대로 둔다 — 로그아웃한 문이 그것을 다시 읽어 초대 단계를 연다.
          switch_to={() => void sign_out()}
        />
      </Suspense>
    )
  }
  if (sharing !== null) {
    const done = () => {
      // 주소창의 `/share?…` 를 치운다 — 새로 고침이 같은 글을 또 들고 오면 안 된다.
      globalThis.history?.replaceState(null, '', '/')
      set_sharing(null)
    }
    return (
      <Suspense fallback={null}>
        <SharePick
          text={sharing}
          channels={(channels.data ?? [])
            .filter((c) => c.name !== null && !c.stored)
            .map((c) => ({ id: c.id, name: c.name ?? '' }))}
          pick={(id) => {
            // 쓰다 만 글이 있으면 뒤에 붙인다 — 덮으면 사람 글이 사라진다.
            const book = new DraftBook(globalThis.localStorage, `maru.drafts.${me}`)
            const before = book.of(id)
            book.put(id, before === '' ? sharing : `${before}\n${sharing}`)
            set_start(id)
            done()
          }}
          cancel={done}
        />
      </Suspense>
    )
  }
  return (
    <Shell platform={platform} session={session}>
      {view === 'settings' ? (
        <Suspense fallback={null}>
          <Settings
            key={trip}
            session={session}
            seat={seat}
            channels={channels.data ?? []}
            keep={platform.keep}
            close={close_settings}
            {...(tab === undefined ? {} : { start: tab })}
            {...(aim === undefined ? {} : { aim })}
            on_tab={(to) => put_hash(settings_hash(to))}
            signed_out={() => {
              // 다음 사람이 앞 사람의 설정 탭으로 들어오지 않게.
              put_hash('')
              leave()
            }}
            leaving={() => drop_push(session.api, platform.keep)}
            {...(platform.place === undefined ? {} : { place: platform.place })}
            device={platform}
          />
        </Suspense>
      ) : (
        <Room
          session={session}
          me={me}
          platform={platform}
          start={start}
          settings={open_settings}
          sign_out={sign_out}
          {...(org === undefined ? {} : { org })}
        />
      )}
    </Shell>
  )
}

export function Gate(props: GateProps) {
  const { auth, seat } = props
  // 초대로 막 들어왔으면 토큰은 끝났다(F232). 주소창을 `/` 로 치우는 것만으로는 모자란다 — 바뀌는 입력이 없어
  // React Compiler 가 아래 두 값을 붙들어 두고, 링크로 막 들어온 사람에게 "로그아웃하고 초대 받기" 가 섰다.
  const [used, set_used] = useState(false)
  const invite_token = used
    ? null
    : props.invite_token === undefined
      ? invite_in(globalThis.location)
      : props.invite_token
  const join_token = used
    ? null
    : props.join_token === undefined
      ? join_in(globalThis.location)
      : props.join_token
  // 동의 부탁은 **들어온 뒤에** 그린다. 로그인부터 시켜야 누가 주는지가 정해진다.
  const consent = props.consent === undefined ? consent_in(globalThis.location) : props.consent
  const shared = props.shared === undefined ? share_in(globalThis.location) : props.shared
  // `null` 은 "아직 모른다" 다. 여기서 곧바로 로그인 화면을 그리면 이미 들어와
  // 있던 사람에게 로그인 화면이 한 번 번쩍인다.
  const [inside, set_inside] = useState<boolean | null>(null)
  const [stranded, set_stranded] = useState(false)
  // `/?new` — 들어와 있는 사람이 워크스페이스를 하나 더 만든다(3차 F88). 쿠키로 곧장 들어가지 않고 가입부터.
  // 첫 상태로 한 번만 읽는다 — 효과에서 읽으면 주소를 치운 뒤의 두 번째 실행이 쿠키로 들어가 버린다.
  const [adding] = useState(() => globalThis.location?.search === '?new')
  const [stage, set_stage] = useState<Stage>({ at: 'email', making: adding })
  const [busy, set_busy] = useState(false)
  const [error, set_error] = useState<string | null>(null)
  const [pending, set_pending] = useState<{ email: string; code: string } | null>(null)
  // 2단계 티켓은 화면 단계(`Stage`)에 싣지 않는다 — 화면은 그 글자를 볼 일이 없다.
  const [ticket, set_ticket] = useState<string | null>(null)
  // 로그인 도중 설정을 끝낸 사람은 백업 코드를 적을 동안 토큰을 여기 붙들어 둔다.
  const [held, set_held] = useState<SignedIn | null>(null)
  const [enroll, set_enroll] = useState(false)
  const [join_dead, set_join_dead] = useState(false)
  // 조직 주소로 왔다면 어느 조직인지(`/w/{slug}`·`{slug}.도메인`). 고르기일 뿐 권한이 아니다 — 들어갈 수
  // 있는지는 서버의 소속 목록이 정한다. 처리는 첫 화면 묶음 밖(`org_route.ts`)에 두고 필요할 때만 읽는다.
  const [aim, set_aim] = useState<OrgDoor | null>(null)
  const { org_slug, org_domain } = props
  useEffect(() => {
    const path = globalThis.location?.pathname ?? ''
    if (org_slug === null || (org_slug === undefined && !org_domain && !path.startsWith('/w/')))
      return
    let alive = true
    void import('./org_route.ts')
      .then((m) => m.find_aim(auth, org_slug, org_domain))
      .then((door) => alive && set_aim(door))
    return () => {
      alive = false
    }
  }, [auth, org_slug, org_domain])
  // 들어와 있는데 주소가 가리키는 조직이 지금 조직과 다르면 갈아탄다.
  useEffect(() => {
    if (inside === true && aim !== null)
      void import('./org_route.ts').then((m) => m.go_aim(seat, aim.tenant))
  }, [inside, aim, seat])
  // 서버에 Google 클라이언트가 없으면 단추를 그리지 않는다. 묻는 동안도 안 그린다.
  const [google_on, set_google_on] = useState(false)

  // 다크 모드·표시는 기기의 것이라 로그인 전에도 건다 — 로그인 화면만 하얗게 번쩍이지 않게.
  useEffect(() => {
    let alive = true
    void load_scheme(props.platform.keep).then((s) => {
      const root = doc_root()
      if (alive && root !== null) apply_scheme(root, s)
    })
    void boot_display(props.platform.keep).catch(() => {})
    return () => {
      alive = false
    }
  }, [props.platform.keep])

  useEffect(() => {
    // 주소는 바로 치운다 — 새로 고침이 다시 가입 화면을 열지 않게. 뒤로 가면 원래 워크스페이스다.
    if (adding) {
      globalThis.history?.replaceState(null, '', '/')
      set_inside(false)
      return
    }
    // 쿠키가 없거나 죽었을 때(401·404)만 로그인 화면이다. 망이 끊겼거나 서버가 5xx 면 로그아웃이 아니다 —
    // 배포 중 새로 고친 사람 모두를 로그인 화면으로 보내지 않게, 기다리며 다시 묻는다(3차 F243).
    let alive = true
    let tries = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const ask = () => {
      seat
        .resume()
        .then((ok) => alive && set_inside(ok))
        .catch(() => {
          if (!alive) return
          set_stranded(true)
          timer = setTimeout(ask, RESUME_RETRY_MS[Math.min(tries, RESUME_RETRY_MS.length - 1)])
          tries += 1
        })
    }
    ask()
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [seat, adding])

  // IdP 에서 돌아왔으면 끝낸다. 주소창의 코드·state 는 한 번 쓰면 죽으니 곧바로 치운다.
  useEffect(() => {
    if (inside !== false) return
    const back = sso_callback_in(globalThis.location)
    if (back === null) return
    globalThis.history?.replaceState(null, '', '/')
    if ('denied' in back) {
      set_error(translate(locale_now(), msg('SSO 로그인을 완료하지 못했어요. 다시 시도해 주세요.')))
      return
    }
    let alive = true
    set_busy(true)
    auth
      .sso_finish(back.state, back.code)
      .then((done) => {
        if (!alive) return
        // SSO 는 그 조직으로 들어간다 — 고르개를 띄우지 않는다.
        seat.hold(done)
        set_inside(true)
      })
      .catch((e: unknown) => {
        if (!alive) return
        set_error(
          e instanceof ApiError && e.status === 404
            ? translate(
                locale_now(),
                msg(
                  '이 워크스페이스에 SSO로 로그인할 계정이 없어요. 관리자에게 초대를 요청해 주세요.',
                ),
              )
            : why(e),
        )
      })
      .finally(() => alive && set_busy(false))
    return () => {
      alive = false
    }
  }, [inside, auth, seat])

  useEffect(() => {
    if (inside !== false) return
    let alive = true
    auth
      .google_enabled()
      .then((on) => alive && set_google_on(on))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [inside, auth])

  // Google 에서 웹으로 돌아왔으면 끝낸다. 주소창의 코드·state 는 곧바로 치운다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: google_back 은 렌더마다 새로 지어지지만 쓰는 값(auth·seat·setter)은 안 바뀐다. 넣으면 렌더마다 다시 구독한다.
  useEffect(() => {
    if (inside !== false) return
    const back = google_callback_in(globalThis.location)
    if (back === null) return
    globalThis.history?.replaceState(null, '', '/')
    google_back(back)
  }, [inside])

  // 앱은 시스템 브라우저에서 끝내고 딥링크로 돌아온다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: google_back 은 렌더마다 새로 지어지지만 쓰는 값(auth·seat·setter)은 안 바뀐다. 넣으면 렌더마다 다시 구독한다.
  useEffect(() => {
    const hear = props.platform.auth_back
    if (inside !== false || hear === undefined) return
    return hear((url) => {
      const back = google_link_in(url)
      if (back !== null) google_back(back)
    })
  }, [inside, props.platform])

  function google_back(back: { code: string; state: string } | { denied: true }) {
    const secret = google_secret_take()
    if ('denied' in back || secret === null) {
      set_error(
        translate(locale_now(), msg('Google 로그인을 완료하지 못했어요. 다시 시도해 주세요.')),
      )
      return
    }
    set_busy(true)
    set_error(null)
    auth
      .google_finish(back.state, back.code, secret)
      .then(async (res: GoogleDone) => {
        if (is_google_signup(res)) {
          // 처음 보는 주소 — 가입 단계로. 주소는 Google 이 확인했으니 코드를 채워 둔다.
          set_stage({ at: 'code', email: res.signup.email, making: true, code: res.signup.code })
          return
        }
        const { email, ...done } = res
        await first(done, email, '')
      })
      .catch((e: unknown) => {
        set_error(
          e instanceof ApiError && e.status === 404
            ? translate(locale_now(), msg('Google 로그인을 완료하지 못했어요. 다시 시도해 주세요.'))
            : e instanceof ApiError && e.status === 409
              ? translate(
                  locale_now(),
                  msg(
                    '이 Google 계정은 다른 마루 계정에 이미 연결돼 있어요. 원래 계정으로 로그인해 주세요.',
                  ),
                )
              : why(e),
        )
      })
      .finally(() => set_busy(false))
  }

  function google_go() {
    void guard(async () => {
      const app = props.platform.auth_back !== undefined
      const { url } = await auth.google_start(app ? 'app' : 'web', google_secret_new())
      if (app) await props.platform.open_url(url)
      else globalThis.location?.assign(url)
    })
  }

  // 초대 링크로 왔으면 로그인 화면 대신 초대 단계다. 링크가 죽었으면 그냥 로그인 화면.
  useEffect(() => {
    if (inside !== false || invite_token === null) return
    let alive = true
    auth
      .invite_preview(invite_token)
      .then((p) => {
        if (!alive) return
        set_stage({
          at: 'invite',
          email: p.email,
          role: p.role,
          has_account: p.has_account,
          code_sent: false,
          ...(p.org_name ? { org_name: p.org_name } : {}),
          ...(p.inviter ? { inviter: p.inviter } : {}),
        })
      })
      .catch((e: unknown) => alive && set_error(why(e)))
    return () => {
      alive = false
    }
  }, [inside, invite_token, auth])

  /** 초대로 막 들어왔다 — 메일 초대와 공유 링크가 같은 끝을 쓴다. */
  function welcomed(pair: SignedIn) {
    // 주소창의 토큰은 한 번 썼으니 지운다 — 새로고침이 죽은 링크를 다시 열지 않게.
    globalThis.history?.replaceState(null, '', '/')
    set_used(true)
    // 사이드바 맨 위의 워크스페이스 이름은 이 목록에서 온다(3차 F79).
    void keep_orgs(props.platform.keep, pair.memberships)
    seat.hold(pair)
    set_enroll(pair.enroll_2fa === true)
    set_inside(true)
  }

  function arrive(done: SignedIn, email: string, code: string, made = false) {
    // 조직 목록은 로그인할 때만 온다 — 사이드바의 이름·전환 메뉴가 새로 고친 뒤에도 쓰게 적어 둔다.
    void keep_orgs(props.platform.keep, done.memberships)
    // 조직이 둘 이상이면 서버가 첫 번째로 들여보냈다. 사람에게 고르게 한다. 조직 주소로 왔고 그 조직의
    // 사람이면 고르개를 건너뛴다 — 들어간 뒤 위의 효과가 그 조직으로 갈아탄다.
    const aimed = aim !== null && done.memberships.some((m) => m.tenant === aim.tenant)
    if (!made && !aimed && done.memberships.length > 1) {
      set_pending({ email, code })
      set_stage({ at: 'org', orgs: done.memberships })
      return
    }
    seat.hold(done)
    set_inside(true)
  }

  /** 첫 요소를 통과했다. 2단계가 남았으면 그 단계로, 아니면 들어간다. */
  async function first(done: LoginResult, email: string, code: string) {
    if (!is_second(done)) {
      arrive(done, email, code)
      return
    }
    set_ticket(done.ticket)
    if (done.second_factor === 'verify') {
      set_stage({ at: 'second', email, mode: 'verify' })
      return
    }
    set_stage({ at: 'second', email, mode: 'enroll' })
    const setup = await auth.login_second_setup(email, done.ticket)
    set_stage({ at: 'second', email, mode: 'enroll', setup })
  }

  async function guard(work: () => Promise<void>) {
    set_busy(true)
    set_error(null)
    try {
      await work()
    } catch (e) {
      set_error(why(e))
    }
    set_busy(false)
  }

  if (inside === null) return <Waiting stranded={stranded} />
  if (inside) {
    return (
      <Inside
        {...props}
        consent={consent}
        enroll={enroll}
        shared={shared}
        invite={
          invite_token !== null
            ? { token: invite_token, join: false }
            : join_token !== null
              ? { token: join_token, join: true }
              : null
        }
        leave={() => {
          // 다음 사람에게 앞사람의 조직 목록·색·언어가 남지 않게. 다크 모드는 기기의 것이라 둔다.
          void forget_orgs(props.platform.keep)
          const root = doc_root()
          if (root !== null) reset_look(root)
          set_locale('ko')
          set_stage({ at: 'email' })
          set_inside(false)
        }}
      />
    )
  }

  return (
    <Suspense fallback={<Waiting />}>
      {join_token !== null && !join_dead ? (
        // 공유 초대 링크(G30)는 제 청크에서 돈다 — 문에 두면 첫 화면이 +0.5 kB 무거워졌다.
        // 링크가 죽었으면 여느 로그인 화면으로 떨어진다(메일 초대와 같다).
        <JoinGate
          auth={auth}
          token={join_token}
          enter={welcomed}
          dead={(e) => {
            set_error(e)
            set_join_dead(true)
          }}
        />
      ) : (
        <SignIn
          org_name={aim?.org_name}
          stage={stage}
          busy={busy}
          error={error}
          on_email={(email, making) =>
            void guard(async () => {
              await (making ? auth.signup_start(email) : auth.login_start(email))
              set_stage({ at: 'code', email, making })
            })
          }
          on_resend={() =>
            void guard(async () => {
              if (stage.at !== 'code') return
              await (stage.making ? auth.signup_start(stage.email) : auth.login_start(stage.email))
            })
          }
          on_code={(code) =>
            void guard(async () => {
              if (stage.at !== 'code') return
              await first(await auth.login_code(stage.email, code), stage.email, code)
            })
          }
          on_password={(email, password) =>
            void guard(async () => {
              await first(await auth.login_password(email, password), email, '')
            })
          }
          on_signup={(f) =>
            void guard(async () => {
              if (stage.at !== 'code') return
              const done = await auth.signup_finish({
                email: stage.email,
                code: f.code,
                slug: f.slug,
                org_name: f.org_name,
                // 첫 워크스페이스 이름은 묻지 않는다. 나중에 바꿀 수 있는 것을 가입
                // 화면에서 물으면 칸만 늘고 포기하는 사람이 는다.
                workspace_name: '일반',
                display_name: f.display_name,
              })
              // 방금 만든 곳으로 들어간다 — 토큰도 그 조직 것이다. 조직이 둘이라고 고르개를 띄우면
              // 방금 만든 이름을 한 번 더 고르게 된다(3차 F220).
              arrive(done, stage.email, f.code, true)
            })
          }
          // 주소는 이미 `/` 로 치웠다 — 다시 읽으면 쿠키로 원래 워크스페이스에 들어간다.
          on_home={adding ? () => globalThis.location?.assign('/') : undefined}
          on_org={(tenant) =>
            void guard(async () => {
              if (!pending) return
              // 코드는 한 번 쓰면 없어진다. 그래서 고른 조직으로 **갈아타기**를 쓴다.
              await seat.switch_org(tenant)
              set_inside(true)
            })
          }
          on_second={(code) =>
            void guard(async () => {
              if (stage.at !== 'second' || ticket === null) return
              const done = await auth.login_second(stage.email, ticket, code)
              set_ticket(null)
              const { backup_codes, ...signed } = done
              if (backup_codes === undefined) {
                arrive(signed, stage.email, code)
                return
              }
              set_held(signed)
              set_stage({ ...stage, backups: backup_codes })
            })
          }
          on_backups_done={() => {
            if (held === null || stage.at !== 'second') return
            set_held(null)
            arrive(held, stage.email, '')
          }}
          on_back={() => {
            set_error(null)
            set_ticket(null)
            // 재설정에서 돌아가면 온 자리(비밀번호 로그인)로, 친 주소를 채운 채(3차 F206).
            set_stage(
              stage.at === 'forgot'
                ? { at: 'email', email: stage.email, by_password: true }
                : { at: 'email' },
            )
          }}
          on_forgot={(email) => {
            set_error(null)
            set_stage({ at: 'forgot', email, sent: false })
          }}
          on_google={google_on ? google_go : undefined}
          on_sso_open={() => {
            set_error(null)
            set_stage({ at: 'sso' })
          }}
          on_sso={(org) =>
            void guard(async () => {
              const { url } = await auth.sso_start(org)
              // IdP 로 떠난다. 돌아오는 자리는 `/sso/callback` 이다.
              globalThis.location?.assign(url)
            })
          }
          on_forgot_code={(email) =>
            void guard(async () => {
              await auth.password_forgot(email)
              // 없는 주소에도 204 다. 여기서 갈라 보이면 **주소가 있는지 알려 주는 창구**가 된다.
              set_stage({ at: 'forgot', email, sent: true })
            })
          }
          on_reset={(code, fresh) =>
            void guard(async () => {
              if (stage.at !== 'forgot') return
              await auth.password_reset(stage.email, code, fresh)
              // 새 비밀번호로 곧장 들여보내지 않는다 — 바꾼 사람이 정말 그 사람인지는
              // 로그인이 한 번 더 확인한다. 다만 주소는 채운 채 비밀번호 칸을 연다(3차 F206).
              set_stage({ at: 'email', email: stage.email, by_password: true, reset: true })
            })
          }
          on_invite_code={() =>
            void guard(async () => {
              if (stage.at !== 'invite' || invite_token === null) return
              // `stage.email` 은 가린 꼴이다 — 그 주소로 코드를 청하면 없는 곳으로 간다(3차 F82).
              await auth.invite_code(invite_token)
              set_stage({ ...stage, code_sent: true })
            })
          }
          on_invite_accept={(code, display_name) =>
            void guard(async () => {
              if (stage.at !== 'invite' || invite_token === null) return
              welcomed(await auth.invite_accept({ token: invite_token, code, display_name }))
            })
          }
        />
      )}
    </Suspense>
  )
}
